import {
	EPaymentStatus,
	EProductStatus,
	SERVICE_STATUSES,
	GPSR_SERVICE_STATUSES_FILTER_COLORS,
	TGpsrItem,
	EConfirmationStatus,
} from 'redux/reducers/gpsr/types';
import { DATES, DATES_ID } from 'services/constants/dates-constants';
import { ETableStructureAlign, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { TUser } from 'redux/reducers/user/types';
import { PAYMENT_STATUS_NAMES, PRODUCT_STATUS_NAMES } from './constants';
import { TSalesProcessingRow } from '../../../redux/reducers/salesProcessing/types';
import { TGpsrServiceStatuses } from 'redux/reducers/gpsr/types';

export const gpsrTableStructure = ({
	usersList,
	serviceStatuses,
}: {
	usersList: TUser[];
	serviceStatuses: TGpsrServiceStatuses;
}): TTableStructure<keyof TGpsrItem>[] => {
	const serviceStatusesList: {
		id: number;
		name: string;
		displayName: string;
		description: string;
		color: string;
	}[] =
		serviceStatuses?.map((status) => ({
			id: status.id,
			name: status.name,
			displayName: status.displayName,
			description: status.description,
			color: SERVICE_STATUSES.find((s) => s.id === status.id)?.color || 'info.light',
		})) || [];
	const serviceStatusColors = GPSR_SERVICE_STATUSES_FILTER_COLORS.map((color): [string, string] => [
		serviceStatuses.find((status) => status.name === color[0])?.displayName || '',
		color[1],
	]);
	return [
		{
			name: 'ID',
			selector: 'id' as keyof TGpsrItem,
			show: false,
			showInFoolData: false,
			sortable: true,
			filterable: false,
			type: ETableStructureType.NUMBER,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},
		{
			name: 'EM ID',
			selector: 'emId' as keyof TGpsrItem,
			show: false,
			showInFoolData: false,
			sortable: true,
			filterable: false,
			type: ETableStructureType.LINK,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},
		{
			name: 'EM Name',
			selector: 'emName' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			searchable: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.EM,
			value: (item: TGpsrItem) => {
				return { id: item.emId, name: item.emName };
			},
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Customer Email',
			selector: 'customerEmail' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: false,
			filterable: false,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Customer Phone',
			selector: 'customerPhone' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: false,
			filterable: false,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Period Start',
			selector: 'periodStart' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: [
				[DATES_ID.TODAY, DATES.TODAY],
				[DATES_ID.YESTERDAY, DATES.YESTERDAY],
				[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
				[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
				[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
				[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
				[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
				[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
			],

			type: ETableStructureType.DATE,
			align: ETableStructureAlign.CENTER,
			colSpan: 1,
		},
		{
			name: 'Period End',
			selector: 'periodEnd' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: [
				[DATES_ID.TODAY, DATES.TODAY],
				[DATES_ID.YESTERDAY, DATES.YESTERDAY],
				[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
				[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
				[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
				[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
				[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
				[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
			],

			type: ETableStructureType.DATE,
			align: ETableStructureAlign.CENTER,
			colSpan: 1,
		},
		{
			name: 'Created At',
			selector: 'createdAt' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: [
				[DATES_ID.TODAY, DATES.TODAY],
				[DATES_ID.YESTERDAY, DATES.YESTERDAY],
				[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
				[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
				[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
				[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
				[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
				[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
			],

			type: ETableStructureType.DATE,
			align: ETableStructureAlign.CENTER,
			colSpan: 1,
		},
		{
			name: 'Invoice ID',
			selector: 'invoiceId' as keyof TGpsrItem,
			show: false,
			showInFoolData: false,
			sortable: true,
			filterable: false,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Invoice Number',
			selector: 'invoiceNumber' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.INVOICE_NUMBER,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},

		{
			name: 'Subscription Plan',
			selector: 'subscriptionPlanName' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.TEXT,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},

		{
			name: 'Additional Product Types',
			selector: 'numberOfAdditionalProductTypes' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.NUMBER,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},

		{
			name: 'Amount',
			selector: 'amount' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: false,
			filterable: false,
			type: ETableStructureType.AMOUNT,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},
		{
			name: 'New Subscription Plan',
			selector: 'newSubscriptionPlanName' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.TEXT,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'New Additional Product Types',
			selector: 'newNumberOfAdditionalProducts' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.NUMBER,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},

		{
			name: 'Payment Status',
			selector: 'paymentStatus' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: [
				[EPaymentStatus.PAID, PAYMENT_STATUS_NAMES[EPaymentStatus.PAID]],
				[EPaymentStatus.PENDING, PAYMENT_STATUS_NAMES[EPaymentStatus.PENDING]],
				[EPaymentStatus.PARTIALLY_PAID, PAYMENT_STATUS_NAMES[EPaymentStatus.PARTIALLY_PAID]],
			],
			colors: [
				[EPaymentStatus.PAID, 'success.light'],
				[EPaymentStatus.PENDING, 'warning.light'],
				[EPaymentStatus.PARTIALLY_PAID, 'warning.light'],
			],
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Products Added',
			selector: 'productsAdded' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: false,
			filterable: false,
			type: ETableStructureType.NUMBER,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},
		{
			name: 'Products Finished',
			selector: 'productsFinished' as keyof TGpsrItem,
			show: false,
			showInFoolData: true,
			sortable: false,
			filterable: false,
			type: ETableStructureType.NUMBER,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},
		{
			name: 'Product Status',
			selector: 'gpsrStatus' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: [
				[EProductStatus.EAS_NOT_STARTED, PRODUCT_STATUS_NAMES[EProductStatus.EAS_NOT_STARTED]],
				[EProductStatus.EM_NOT_STARTER, PRODUCT_STATUS_NAMES[EProductStatus.EM_NOT_STARTER]],
				[EProductStatus.EM_SKU_STARTED, PRODUCT_STATUS_NAMES[EProductStatus.EM_SKU_STARTED]],
				[EProductStatus.EM_NOT_FINISHED, PRODUCT_STATUS_NAMES[EProductStatus.EM_NOT_FINISHED]],
				[EProductStatus.ALL_IS_DONE, PRODUCT_STATUS_NAMES[EProductStatus.ALL_IS_DONE]],
			],
			colors: [
				[EProductStatus.EAS_NOT_STARTED, 'warning.light'],
				[EProductStatus.EM_NOT_STARTER, 'warning.light'],
				[EProductStatus.EM_SKU_STARTED, 'warning.light'],
				[EProductStatus.EM_NOT_FINISHED, 'warning.light'],
				[EProductStatus.ALL_IS_DONE, 'success.light'],
			],
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Service Status',
			selector: 'serviceStatusId' as keyof TGpsrItem,
			show: false,
			showInFoolData: false,
			sortable: true,
			filterable: true,
			filter: serviceStatuses.map((status): [string, string] => [
				String(status.id),
				status.displayName,
			]),

			type: ETableStructureType.NUMBER,
			align: ETableStructureAlign.RIGHT,
			colSpan: 1,
		},
		{
			name: 'Service Status',
			selector: 'serviceStatusName' as keyof TGpsrItem,
			show: true,
			showInFoolData: false,
			sortable: true,
			filterable: false,
			filter: serviceStatuses.map((status): [string, string] => [
				String(status.id),
				status.displayName,
			]),
			colors: serviceStatusColors,
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
			tooltip: (item: TGpsrItem) =>
				serviceStatuses.find((status) => status.id === item.serviceStatusId)?.description || '',
			value: (item: TGpsrItem) =>
				serviceStatuses.find((status) => status.id === item.serviceStatusId)?.displayName || '',
		},
		{
			name: 'Author',
			selector: 'author' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.USER,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Updated At',
			selector: 'updatedAt' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: [
				[DATES_ID.TODAY, DATES.TODAY],
				[DATES_ID.YESTERDAY, DATES.YESTERDAY],
				[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
				[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
				[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
				[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
				[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
				[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
			],

			type: ETableStructureType.DATE,
			align: ETableStructureAlign.CENTER,
			colSpan: 1,
		},
		{
			name: 'Updated By',
			selector: 'updatedBy' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: true,
			filter: usersList?.map((user: TUser) => [user.id, user.fullName]) || [],
			type: ETableStructureType.USER,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
		{
			name: 'Confirmation Status',
			selector: 'confirmationStatus' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
			filter: [
				[EConfirmationStatus.CONFIRMED, 'Confirmed'],
				[EConfirmationStatus.WAITING_CONFIRMATION, 'Waiting Confirmation'],
			],
			colors: [
				[EConfirmationStatus.CONFIRMED, 'success.light'],
				[EConfirmationStatus.WAITING_CONFIRMATION, 'warning.light'],
			],
		},
		{
			name: 'Confirmed By',
			selector: 'confirmedBy' as keyof TGpsrItem,
			show: true,
			showInFoolData: true,
			sortable: true,
			filterable: false,
			type: ETableStructureType.USER,
			align: ETableStructureAlign.LEFT,
			colSpan: 1,
		},
	];
};
